import type { LibraryPosition } from "@shared/schema";
import { libraryLookupKey } from "@shared/library-pricing";

export type PlausibilitySource = "lv" | "manual" | "photo-agg";

export interface CheckPosition {
  /** Eindeutiger Schlüssel pro Eingangsposition (entspricht typ. quote item id). */
  key: string;
  source: PlausibilitySource;
  /** Falls zuordenbar: ID der LV-Position für den Sprung im Kalkulator. */
  lvPositionId?: string;
  name: string;
  unit: string;
  ep: number;
  qty: number;
}

export type PlausibilityFindingType =
  | "similar-different-ep"
  | "ep-deviates-from-library"
  | "missing-value"
  | "same-position-different-unit";

export interface BaseFinding {
  /** Stabile ID für React-Keys etc. */
  id: string;
  type: PlausibilityFindingType;
  /** Kurze Überschrift. */
  title: string;
  /** Erklärung inkl. Vorschlag. */
  description: string;
  positions: CheckPosition[];
}

export interface SimilarDifferentEpFinding extends BaseFinding {
  type: "similar-different-ep";
  unit: string;
  /** EP-Stichproben: Wert + Anzahl. */
  epStats: Array<{ ep: number; count: number }>;
  suggestedEp: number;
}

export interface EpDeviatesFinding extends BaseFinding {
  type: "ep-deviates-from-library";
  libraryEp: number;
  /** Relative Abweichung; > 0 = teurer als Bibliothek. */
  deviation: number;
  suggestedEp: number;
}

export interface MissingValueFinding extends BaseFinding {
  type: "missing-value";
  missing: "price" | "quantity";
}

export interface SameDifferentUnitFinding extends BaseFinding {
  type: "same-position-different-unit";
  units: string[];
}

export type PlausibilityFinding =
  | SimilarDifferentEpFinding
  | EpDeviatesFinding
  | MissingValueFinding
  | SameDifferentUnitFinding;

/** Schwellenwert für „starke" Abweichung vom Bibliothekspreis. */
export const EP_DEVIATION_THRESHOLD = 0.25;

/** Maximale Levenshtein-Distanz zur Erkennung „ähnlicher" Bezeichnungen. */
const SIMILAR_LEVENSHTEIN_MAX = 2;

/**
 * Vergleichs-Normalisierung: lowercase, Umlaute behalten, Sonderzeichen und
 * Whitespace zusammenfassen, Mehrfach-Spaces glätten.
 */
export function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = a.length;
  const n = b.length;
  let prev = new Array<number>(n + 1);
  let curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/** Vorschlags-EP: häufigster Wert; bei Mehrfachgleichstand → Median. */
export function pickSuggestedEp(eps: number[]): number {
  if (eps.length === 0) return 0;
  const counts = new Map<number, number>();
  eps.forEach((raw) => {
    const ep = Math.round(raw * 100) / 100;
    counts.set(ep, (counts.get(ep) ?? 0) + 1);
  });
  const maxCount = Math.max(...Array.from(counts.values()));
  const top = Array.from(counts.entries())
    .filter(([, c]) => c === maxCount)
    .map(([ep]) => ep);
  if (top.length === 1) return top[0];
  return Math.round(median(top) * 100) / 100;
}

/** Heuristische Ähnlichkeit zweier normalisierter Bezeichnungen. */
function namesAreSimilar(a: string, b: string): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const len = Math.max(a.length, b.length);
  // Quick exit: ein Name enthält den anderen mit kleinem Längen-Delta.
  if (a.includes(b) || b.includes(a)) {
    return Math.abs(a.length - b.length) <= 3;
  }
  // Bei sehr unterschiedlichen Längen lohnt sich Levenshtein nicht.
  if (Math.abs(a.length - b.length) > SIMILAR_LEVENSHTEIN_MAX + 2) return false;
  const allowed = Math.min(
    SIMILAR_LEVENSHTEIN_MAX,
    Math.max(1, Math.floor(len * 0.15)),
  );
  return levenshtein(a, b) <= allowed;
}

function formatEur(n: number): string {
  return n
    .toFixed(2)
    .replace(".", ",");
}

/**
 * Plausibilitäts-Check für eine konsolidierte Liste von Quote-Positionen.
 * Reine Funktion — keine Seiteneffekte, keine UI-Abhängigkeit. Liefert
 * gruppierte, typisierte Befunde mit Beschreibung und Vorschlagswert.
 */
export function runPlausibilityChecks(
  positions: CheckPosition[],
  libraryPositions: LibraryPosition[] = [],
): PlausibilityFinding[] {
  const findings: PlausibilityFinding[] = [];
  // Aktive Positionen: alles, was in die Summe einfließt (qty>0 ODER ep>0).
  // „Komplett leere" Default-Zeilen (qty=0 UND ep=0) werden ignoriert, sonst
  // würden noch unbearbeitete LV-Defaults Warnungen erzeugen.
  const active = positions.filter((p) => p.qty > 0 || p.ep > 0);

  // ── 1) Position ohne Preis / ohne Menge ────────────────────────────────
  active.forEach((p) => {
    if (p.ep <= 0) {
      findings.push({
        id: `missing-price-${p.key}`,
        type: "missing-value",
        missing: "price",
        title: "Position ohne Preis",
        description: `„${p.name}" hat einen Einheitspreis von 0 €. Bitte EP hinterlegen oder Position deaktivieren.`,
        positions: [p],
      });
    } else if (p.qty <= 0) {
      findings.push({
        id: `missing-qty-${p.key}`,
        type: "missing-value",
        missing: "quantity",
        title: "Position ohne Menge",
        description: `„${p.name}" hat eine Menge von 0 ${p.unit}. Bitte Menge hinterlegen oder Position deaktivieren.`,
        positions: [p],
      });
    }
  });

  // ── 2) Ähnliche Positionen mit unterschiedlichem EP ────────────────────
  // Gruppieren nach normalisiertem Namen + Einheit; danach ähnliche
  // Buckets gleicher Einheit fuzzy zusammenführen.
  const normalizedGroups = new Map<string, CheckPosition[]>();
  active.forEach((p) => {
    if (p.ep <= 0 || p.qty <= 0) return; // unvollständig → nicht in EP-Vergleich
    const key = `${normalizeName(p.name)}|${p.unit.trim().toLowerCase()}`;
    const arr = normalizedGroups.get(key) ?? [];
    arr.push(p);
    normalizedGroups.set(key, arr);
  });

  const visited = new Set<string>();
  const buckets: CheckPosition[][] = [];
  const groupsList = Array.from(normalizedGroups.entries());
  for (let i = 0; i < groupsList.length; i++) {
    const [k1, items1] = groupsList[i];
    if (visited.has(k1)) continue;
    visited.add(k1);
    const bucket = [...items1];
    const [n1, u1] = k1.split("|");
    for (let j = i + 1; j < groupsList.length; j++) {
      const [k2, items2] = groupsList[j];
      if (visited.has(k2)) continue;
      const [n2, u2] = k2.split("|");
      if (u1 !== u2) continue;
      if (namesAreSimilar(n1, n2)) {
        bucket.push(...items2);
        visited.add(k2);
      }
    }
    if (bucket.length >= 2) buckets.push(bucket);
  }

  buckets.forEach((bucket, idx) => {
    const eps = bucket.map((p) => Math.round(p.ep * 100) / 100);
    const epSet = new Set(eps);
    if (epSet.size <= 1) return; // alle EPs gleich → kein Befund
    const counts = new Map<number, number>();
    eps.forEach((ep) => counts.set(ep, (counts.get(ep) ?? 0) + 1));
    const epStats = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0] - b[0])
      .map(([ep, count]) => ({ ep, count }));
    const suggested = pickSuggestedEp(eps);
    const unit = bucket[0].unit;
    findings.push({
      id: `similar-${idx}`,
      type: "similar-different-ep",
      title: "Ähnliche Positionen mit abweichendem EP",
      description:
        `${bucket.length} ähnliche Positionen („${bucket[0].name}") mit unterschiedlichen Einheitspreisen. ` +
        `Vorschlag: Einheitlichen EP von ${formatEur(suggested)} €/${unit} setzen.`,
      positions: bucket,
      unit,
      epStats,
      suggestedEp: suggested,
    });
  });

  // ── 3) Gleiche Position, unterschiedliche Einheit ──────────────────────
  const byNameOnly = new Map<string, CheckPosition[]>();
  active.forEach((p) => {
    const key = normalizeName(p.name);
    const arr = byNameOnly.get(key) ?? [];
    arr.push(p);
    byNameOnly.set(key, arr);
  });
  let unitIdx = 0;
  byNameOnly.forEach((items) => {
    const units = Array.from(new Set(items.map((p) => p.unit.trim())));
    if (units.length <= 1) return;
    findings.push({
      id: `unit-${unitIdx++}`,
      type: "same-position-different-unit",
      title: "Gleiche Position mit unterschiedlichen Einheiten",
      description:
        `„${items[0].name}" tritt mit unterschiedlichen Einheiten auf: ${units.join(", ")}. ` +
        `Bitte einheitliche Einheit wählen, sonst sind die Mengen nicht vergleichbar.`,
      positions: items,
      units,
    });
  });

  // ── 4) EP weicht stark vom Bibliothekspreis ab ─────────────────────────
  const libByKey = new Map<string, LibraryPosition>();
  for (const lp of libraryPositions) {
    if (lp.aktiv === false) continue;
    libByKey.set(libraryLookupKey(lp.name, lp.unit), lp);
  }
  active.forEach((p) => {
    if (p.ep <= 0) return;
    const lp = libByKey.get(libraryLookupKey(p.name, p.unit));
    if (!lp || lp.ep <= 0) return;
    const deviation = (p.ep - lp.ep) / lp.ep;
    if (Math.abs(deviation) < EP_DEVIATION_THRESHOLD) return;
    const sign = deviation > 0 ? "höher" : "niedriger";
    const pct = Math.abs(deviation * 100).toFixed(0);
    findings.push({
      id: `dev-${p.key}`,
      type: "ep-deviates-from-library",
      title: "EP weicht stark vom Bibliothekspreis ab",
      description:
        `„${p.name}" hat EP ${formatEur(p.ep)} € — ${pct} % ${sign} als Bibliothekspreis ` +
        `(${formatEur(lp.ep)} €/${lp.unit}). Vorschlag: EP prüfen oder auf ${formatEur(lp.ep)} € setzen.`,
      positions: [p],
      libraryEp: lp.ep,
      deviation,
      suggestedEp: lp.ep,
    });
  });

  return findings;
}

/** Kurz-Zusammenfassung für die Bestätigungsabfrage. */
export function summarizeFindings(findings: PlausibilityFinding[]): {
  total: number;
  byType: Record<PlausibilityFindingType, number>;
} {
  const byType: Record<PlausibilityFindingType, number> = {
    "similar-different-ep": 0,
    "ep-deviates-from-library": 0,
    "missing-value": 0,
    "same-position-different-unit": 0,
  };
  findings.forEach((f) => {
    byType[f.type] += 1;
  });
  return { total: findings.length, byType };
}
